import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useStore } from '@/contexts/StoreContext';
import { useAuth } from '@/contexts/AuthContext';
import { playerAuthService } from '@/lib/services/player-auth.service';
import { Caixa } from '@/construtor/caixa';

const TOKEN_FORMATO = /^[a-f0-9]{64}$/;

/**
 * Página do link "redefinir senha" enviado por e-mail.
 *
 * O token chega depois do `#` (nunca enviado a servidor nem registrado por
 * analytics) e sai da barra de endereço assim que a página abre: não fica no
 * histórico do navegador nem vai junto se a pessoa copiar o endereço.
 */
export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { store } = useStore();
  const { openLoginModal } = useAuth();

  const tokenRef = useRef<string | null>(null);
  if (tokenRef.current === null && typeof window !== 'undefined') {
    tokenRef.current = new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
  }

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [sending, setSending] = useState(false);
  const [state, setState] = useState<'form' | 'invalid' | 'done'>(
    TOKEN_FORMATO.test(tokenRef.current ?? '') ? 'form' : 'invalid',
  );

  useEffect(() => {
    if (window.location.hash || window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!store?.id || !tokenRef.current) return;

    if (password.length < 6) {
      toast.error(t('store.auth.password_too_short'));
      return;
    }
    if (password !== confirm) {
      toast.error(t('store.auth.passwords_mismatch'));
      return;
    }

    setSending(true);
    try {
      await playerAuthService.resetPassword(store.id, tokenRef.current, password);
      tokenRef.current = null;
      setState('done');
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.code === 'RESET_TOKEN_INVALID') {
        setState('invalid');
      } else if (error?.response?.status === 429) {
        toast.error(t('store.auth.forgot_too_many'));
      } else {
        toast.error(data?.message || t('store.auth.reset_error'));
      }
    } finally {
      setSending(false);
    }
  };

  const entrar = () => {
    navigate('/');
    openLoginModal();
  };

  return (
    <div className="container mx-auto flex min-h-[60vh] items-center justify-center px-4 py-12">
      <Caixa id="perfil.redefinir" miolo>
      <Card className="w-full max-w-md bg-card border-border">
        <CardContent className="space-y-5 pt-6">
          {state === 'form' && (
            <>
              <div className="text-center">
                <h1 className="font-display text-2xl font-bold text-foreground">{t('store.auth.reset_title')}</h1>
                <p className="mt-1 text-sm text-muted-foreground">{t('store.auth.reset_subtitle')}</p>
              </div>
              <Caixa id="perfil.redefinir.formulario">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-password">{t('store.auth.new_password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="reset-password"
                      type="password"
                      autoComplete="new-password"
                      className="pl-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={6}
                      maxLength={128}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-confirm">{t('store.auth.confirm_password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="reset-confirm"
                      type="password"
                      autoComplete="new-password"
                      className="pl-10"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      maxLength={128}
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary-hover" disabled={sending}>
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('store.auth.reset_saving')}
                    </>
                  ) : (
                    t('store.auth.reset_button')
                  )}
                </Button>
              </form>
              </Caixa>
            </>
          )}

          {state === 'done' && (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
              <p className="font-semibold text-foreground">{t('store.auth.reset_done')}</p>
              <Button className="w-full bg-primary hover:bg-primary-hover" onClick={entrar}>
                {t('store.auth.login_button')}
              </Button>
            </div>
          )}

          {state === 'invalid' && (
            <div className="space-y-4 text-center">
              <XCircle className="mx-auto h-12 w-12 text-destructive" />
              <p className="font-semibold text-foreground">{t('store.auth.reset_invalid')}</p>
              <Button variant="outline" className="w-full" onClick={entrar}>
                {t('store.auth.reset_request_new')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      </Caixa>
    </div>
  );
}
